/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../lib/@waca/core-client/js/core-client/ui/core/Events',
	'./Model',
	'../utils/CommonUtil'
], function(_, Events, Model, CommonUtil) {

	/**
	 * A Backbone style base Collection Class.
	 *
	 * Use a collection to hold on to and manage a set of models.
	 *
	 */
	var Collection = Events.extend({

		/**
		 * initialize the collection with the specified models
		 * @param models - the models to be set into the collection initially (could be empty)
		 * @param options - the options which include eventing options and a few regular options.
		 *					cbValidator option: if specified, the model will be validated using this callback
		 *										prior to being added to the collection.
		 */
		init: function(models, options) {
			Collection.inherited('init', this, arguments);
			this.models = [];
			this.modelMap = {};

			models = models || [];
			options = options || {};
			var setOptions = Object.assign({}, options, {
				add: true,
				remove: false,
				merge: false
			});
			// The model class that this collection contains
			this.model = this.modelClass || options.modelClass || Model;
			this.set(models, setOptions);

		},

		/**
		 * Returns the models in this collection as a list
		 */
		getModels: function() {
			return this.models;
		},

		/**
		 * Get a model from the collection by ID or the model object itself
		 *
		 * @param	The model object or ID.
		 */
		get: function( objOrId ) {
			var model = this.modelMap[objOrId];
			if( model ) {
				return model;
			}
			if( objOrId ) {
				return this.modelMap[objOrId.id];
			}
		},

		/**
		 * Add a single model or list of models to this collection
		 *
		 * @param models	The model(s) to add to this collection
		 * @param options	Options to apply to the add
		 */
		add: function(models, options) {
			return this.set(models, Object.assign({ merge: false, add: true, remove: false }, options));
		},

		/**
		 * Remove a single model or list of models to this collection
		 *
		 * @param models	The model(s) to remove from this collection
		 * @param options	Options to apply to the remove
		 */
		remove: function(models, options) {
			options = Object.assign({}, options);
			models = (models===undefined || models===null) ? [] : models;
			var singleModel = !Array.isArray(models);
			models = singleModel ? [models] : models;

			var modelsRemoved = [];
			for ( const entry of models ) {
				var model = this.get(entry);
				if ( model ) {
					modelsRemoved.push(model);
					delete this.modelMap[model.id];
					var index = this.models.indexOf(model);
					var modelRemoved = this.models[index];
					this.models.splice(index, 1);

					var senderContext = (options.senderContext)? options.senderContext: {
						applyFn: function(value, sender, name, data) { // jshint ignore:line
							if(value) { // must be removing
								this.remove(model, { sender: sender, silent:false, payloadData: data });
							} else {
								this.add(model, { sender: sender, silent:false, payloadData: data });
							}
						}.bind(this)
					};

					if ( !options.silent ) {
						options.index = index;
						var event = {
							name: 'remove',
							value: model,
							eventName: 'remove',
							collection: this,
							model: model,
							options: options,
							data: options.payloadData,
							sender: options.sender ? options.sender : this,
							senderContext: senderContext
						};
						modelRemoved && modelRemoved.trigger('remove', event);
					}
					modelRemoved && this._stopTrackingModel(modelRemoved);
				}
			}

			return singleModel ? modelsRemoved[0] : modelsRemoved;
		},

		/**
		 *  This method should be used when you want to reset the collection with a list
		 *  of models without getting add/remove events for every modification. This is useful
		 *  for bulk operations. At the end of the reset (unless you pass in options.silent) a reset
		 *  event will be fired.
		 *
		 *  @param models	List of models to reset the collection to
		 *  @param options	The options to apply to the reset
		 *  @returns		The models list after the reset.
		 */
		reset: function(models, options) {
			options = options || {};
			for (var i = 0, length = this.models.length; i < length; i++) {
				this._stopTrackingModel(this.models[i], options);
			}
			options.previousModels = this.models;
			this.models = [];
			this.modelMap = {};
			if ( models ) {
				models = this.add(models, Object.assign({ silent: true }, options));
			}
			if (!options.silent) {
				var event = {
					name: 'reset',
					eventName: 'reset',
					collection: this,
					options: options
				};
				this.trigger('reset', event);
			}
			return models;
		},
		/** This method should be used when you want to reorder the collection
		 *
		 *  @param modelId	id of the model to reorder
		 *  @param beforeId	id of the model to move model before
		 *  @param options	The options to apply to the reorder
		 *  @returns		The models list after the reorder.
		 */

		reorder: function(modelId, beforeId, options){
			options = Object.assign({}, options);

			var model = this.get(modelId);
			var beforeModel = this.get(beforeId);

			if (model && modelId !== beforeId) {
				var index = this.models.indexOf(model);
				var previousBeforeModelId = this.models[index+1] ? this.models[index+1].id : undefined;

				// remove model
				this.models.splice(index, 1);

				var insertBefore = this.models.indexOf(beforeModel);

				//insert to new location
				if( insertBefore >=0 ) {
					this.models.splice(insertBefore, 0, model);
				}else{
					this.models.push(model);
				}

				// only trigger an event if we are asked to and the model actually moved.
				if ((!options.silent &&
					index !== insertBefore &&
					beforeId !== previousBeforeModelId &&
					!(index + 1 === this.models.length &&insertBefore === -1))
					|| options.forceEvent) {

					var senderContext = {
						applyFn:  function(value, sender, name, data) {
							this.reorder(value.modelId, value.beforeId, { sender: sender, silent:false, payloadData: data });
						}.bind(this)
					};

					var event = {
						name: 'reorder',
						eventName: 'reorder',
						collection: this,
						value: { modelId: modelId, beforeId: beforeId },
						prevValue: { modelId: modelId, beforeId: previousBeforeModelId },
						options: options,
						data: options.payloadData,
						sender: options.sender ? options.sender : this,
						senderContext: senderContext
					};
					this.trigger('reorder', event);
				}
			}
		},

		/**
		 * Set the collection to the supplied list of models. Models that are in the supplied
		 * list that aren't in the collection will be added and models that are not supplied will
		 * be removed.
		 *
		 * @param models	The set of models to set the collection with
		 * @param options	The options to apply to the set operation
		 *
		 * @returns the added models.
		 */
		set: function(models, options) {
			options = CommonUtil.defaults({}, options, { add: true, remove: true, merge: true, insert: false });
			models = models || [];
			var singleModel = !Array.isArray(models);
			models = singleModel ? [models] : CommonUtil.clone(models);
			var i, model, existing, length;
			var toAdd = [], toRemove = [], newModelsMap = {};
			var add = options.add, merge = options.merge, remove = options.remove, insert = options.insert;

			for (i = 0, length = models.length; i < length; i++) {
				model = models[i];
				if ( !( model instanceof this.model ) ) {
					models[i] = model = new this.model(model, Object.assign({}, options, this.modelConstructorOptions));
				}

				//If a validator callback has been specified, only add models that pass validation.
				if (options.cbValidator && !options.cbValidator(model)) {
					continue;
				}

				newModelsMap[model.id] = model;
				existing = this.modelMap[model.id];
				if ( merge && existing) {
					// Merge the new model with the existing model
					existing.set(model.toJSON(), options);
				} else if ( add && !existing) {
					// Add the model to this collection
					toAdd.push(model);
				}
			}

			function removeModels() {
				/*jshint validthis:true */
				if ( remove ) {
					for (i = 0, length = this.models.length; i < length; i++) {
						existing = this.models[i];
						if ( !newModelsMap[existing.id] ) {
							toRemove.push(existing);
						}
					}
					if ( toRemove.length ) {
						this.remove(toRemove, options);
					}
				}
			}
			removeModels.bind(this)();

			function addModel(model) {
				/*jshint validthis:true */
				this.modelMap[model.id] = model;
				if (insert) {
					this.models.unshift( model );
				} else {
					this.models.push( model);
				}
				this._trackModel(model);
				var senderContext = (options.senderContext)?options.senderContext:{
					applyFn: function(value, sender, name, data) {
						if(value) { // must be adding
							this.add(model, { sender: sender, silent:false , payloadData: data });
						} else {
							this.remove(model, { sender: sender, silent:false , payloadData: data });
						}
					}.bind(this)
				};
				if ( !options.silent ) {
					var event = {
						name: 'add',
						value: model,
						eventName: 'add',
						collection: this,
						model: model,
						options: options,
						data: options.payloadData,
						sender: options.sender || this,
						senderContext: senderContext
					};
					model.trigger('add', event);
				}
			}
			for (i = 0, length = toAdd.length; i < length; i++ ) {
				addModel.bind(this)(toAdd[i]);
			}

			return singleModel ? models[0] : models;
		},

		/**
		 * Subscribe to the given model's events
		 *
		 * @param model	The model to track
		 */
		_trackModel: function(model) {
			this.modelMap[model.id] = model;
			model.on('all', this._onModelEvent, this);
			model.on('change:id', function(payload){
				delete this.modelMap[payload.prevValue];
				var bExists = false;
				for(const entry of this.models){
					if(payload.value === entry.id  && entry !== model){
						var index = this.models.indexOf(model);
						//Remove the model if the 'new' model with the same 'new' id exists
						this.models.splice(index, 1);
						bExists = true;
						break;
					}
				}
				if(!bExists){
					this.modelMap[payload.value] = model;
				}

			}.bind(this));
		},

		/**
		 * Stop tracking this model
		 *
		 * @param model	The model to stop tracking
		 */
		_stopTrackingModel: function(model) {

			// Remove the collection reference from the model
			if (this === model.collection) {
				delete model.collection;
			}
			model.off('all', this._onModelEvent, this);
		},

		/**
		 * Callback which is fired everytime a tracked model fires an event
		 */
		_onModelEvent: function(event) {
			var eventName = event.eventName;
			if ( (eventName === 'add' || eventName === 'remove') && event.collection !== this ) {
				// This model is being added or removed from another collection, this collection doesn't care
				return;
			}
			CommonUtil.defaults(event, { model: event.sender, collection: this });
			this.trigger(eventName, event);
		},

		/**
		 * Get the collection in a persistable format by calling toJSON on each of
		 * the collection's models.
		 * @param additionalAttrs - an optional array of attributes which can be used to augment the returned JSON with attributes not in the whitelist
		 * @returns JSON "object representation" of the collection.
		 */
		toJSON: function(additionalAttrs) {
			return this.map( function(model) {
				return model.toJSON(additionalAttrs);
			});
		}

	});

	// Mixin each of the below Underscore methods as a proxy to the Collection class.
	Collection._underscoreMethods = ['forEach', 'each', 'map', 'collect', 'reduce', 'foldl',
		'inject', 'reduceRight', 'foldr', 'find', 'detect', 'filter', 'select',
		'reject', 'every', 'all', 'some', 'any', 'include', 'contains', 'invoke',
		'max', 'min', 'toArray', 'size', 'first', 'head', 'take', 'initial', 'rest',
		'tail', 'drop', 'last', 'without', 'difference', 'indexOf', 'shuffle',
		'lastIndexOf', 'isEmpty', 'chain', 'sample', 'partition'];
	Collection._underscoreMethods.forEach( function(method) {
		if (!_[method]) {
			return;
		}
		Collection.prototype[method] = function() {
			var args = Array.prototype.slice.call(arguments, 0);
			args.unshift(this.models);
			return _[method].apply(_, args);
		};
	});

	// Mixin each of the below Underscore methods that take a property name as an argument.
	Collection._underscoreAttributeMethods = ['groupBy', 'countBy', 'sortBy', 'indexBy'];
	Collection._underscoreAttributeMethods.forEach(function(method) {
		if (!_[method]) {
			return;
		}
		Collection.prototype[method] = function(value, context) {
			var iterator = value instanceof Function ? value : function(model) {
				return model.get(value);
			};
			return _[method](this.models, iterator, context);
		};
	});

	return Collection;

});